import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  documentId,
} from "firebase/firestore";
import { db } from "../../firebase";
import FamilyNoteContainerMobile from "../Notes/FamilyNoteContainerMobile";
import MedicalNoteContainerMobile from "../Notes/MedicalNoteContainerMobile";
import { Autocomplete, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import EndOfShiftNoteContainerMobile from "../Notes/EndOfShiftNoteContainerMobile";
import HandoverNotesContainerGlobalMobile from "../Notes/HandoverNotesContainerGlobalMobile";
import GroupNotesContainerGlobalMobile from "../Notes/GroupNotesContainerGlobalMobile";
import NotesContainerGlobalMobile from "../Notes/NotesContainerGlobalMobile";
import { useAuth } from "../../contexts/AuthContext";

function AllNotesContainerMobile(props) {
  const [notes, setNotes] = useState([]);
  const [, setReadCount] = useState(0);
  // const [selectedNoteType, setSelectedNoteType] = useState("");
  const [noteTypes, setNoteTypes] = useState([]);
  const [selectedClientName, setSelectedClientName] = useState("");
  const [clientNames, setClientNames] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedNoteType, setSelectedNoteType] = useState([]); // Initialiser en tant que tableau vide

  const { currentUser } = useAuth();
  const [userRole, setUserRole] = useState("");
  useEffect(() => {
    const notesRef = collection(db, "notes");
    const q = query(notesRef, orderBy("effectiveDateTime", "desc"), limit(30));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const notesData = [];
      const types = new Set();
      querySnapshot.forEach((doc) => {
        const noteData = { id: doc.id, ...doc.data() };
        notesData.push(noteData);
        types.add(noteData.noteType);
      });
      setNotes(notesData);
      setNoteTypes(Array.from(types));
      setReadCount((prevCount) => prevCount + querySnapshot.size);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
      const userId = currentUser.uid;
      const q = query(
        collection(db, "roles"),
        where(documentId(), "==", userId)
      );

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        if (!querySnapshot.empty) {
          const role = querySnapshot.docs[0].data().role;
          setUserRole(role);
        } else {
          console.log("❌ Aucun rôle trouvé.");
        }
      });

      return () => unsubscribe();
    }
  }, [currentUser]);

  const handleStartDateChange = (newValue) => {
    if (endDate && newValue > endDate) {
      setEndDate(newValue);
    }
    setStartDate(newValue);
  };

  const handleEndDateChange = (newValue) => {
    if (startDate && newValue < startDate) {
      setStartDate(newValue);
    }
    setEndDate(newValue);
  };

  const resetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedNoteType([]);
    setSelectedClientName("");
  };

  useEffect(() => {
    if (selectedNoteType.length > 0) {
      const filteredNotesByType = notes.filter((note) =>
        selectedNoteType.includes(note.noteType)
      );
      const uniqueClientNames = Array.from(
        new Set(
          filteredNotesByType.map(
            (note) => note.clientFirstName + " " + note.clientLastName
          )
        )
      );
      setClientNames(uniqueClientNames);
    } else {
      // Si aucun type de note n'est sélectionné, afficher tous les clients
      const allClientNames = Array.from(
        new Set(
          notes.map((note) => note.clientFirstName + " " + note.clientLastName)
        )
      );
      setClientNames(allClientNames);
    }
  }, [selectedNoteType, notes]);

  const filteredNotes = notes.filter((note) => {
    const matchesType = selectedNoteType.length
      ? selectedNoteType.includes(note.noteType)
      : true;

    const matchesClient = selectedClientName
      ? note.clientFirstName + " " + note.clientLastName === selectedClientName
      : true;

    const matchesDate =
      startDate && endDate
        ? note.effectiveDateTime &&
          note.effectiveDateTime.toDate() >= startDate &&
          note.effectiveDateTime.toDate() <= endDate
        : true;

    const isVisibleToUser =
      note.noteType !== "therapyNote" || // Toutes les notes sauf therapyNote sont visibles
      userRole === "Super Admin" ||
      userRole === "Clinical Director" ||
      (userRole === "Therapist" && note.doneBy === currentUser.displayName); // Les therapists ne voient que leurs propres therapyNotes

    return matchesType && matchesClient && matchesDate && isVisibleToUser;
  });

  const formatDate = (date, options) => {
    return date
      ? new Intl.DateTimeFormat("en-EN", options).format(date.toDate())
      : "";
  };

  return (
    <div className="notes-container-global">
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Autocomplete
          multiple
          options={noteTypes}
          getOptionLabel={(option) => option}
          style={{ width: 300, marginBottom: 20 }}
          value={selectedNoteType}
          clearOnEscape
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Note Type"
              variant="outlined"
            />
          )}
          onChange={(event, value) => setSelectedNoteType(value || [])}
        />
        <div style={{ marginLeft: "0px" }}>
          <Autocomplete
            options={clientNames}
            getOptionLabel={(option) => option}
            // value={selectedClientName}
            value={
              clientNames.includes(selectedClientName)
                ? selectedClientName
                : null
            } // Empêche la valeur invalide
            isOptionEqualToValue={(option, value) => option === value} // Empêche le warning
            clearOnEscape
            style={{ width: 300, marginBottom: 20 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Client Name"
                variant="outlined"
              />
            )}
            onChange={(event, value) => setSelectedClientName(value || "")}
          />
        </div>

        <div
          style={{
            marginLeft: "0px",
            display: "flex",
            flexDirection: "row",
            marginBottom: "20px",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={handleStartDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  style={{ marginLeft: "40px", width: 300, marginBottom: 20 }}
                />
              )}
            />
            <div style={{ marginLeft: "20px" }}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    style={{ marginLeft: "40px", width: 300, marginBottom: 20 }}
                  />
                )}
              />
            </div>
          </LocalizationProvider>

          <IconButton
            onClick={resetFilters}
            style={{ marginLeft: "10px", paddingBottom: "15px" }}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      </div>
      {filteredNotes.map((note, index) => {
        const dateOptions = {
          day: "numeric",
          month: "short",
          year: "numeric",
        };

        const dateOptions2 = {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        };

        const timeOptions = {
          hour: "2-digit",
          minute: "2-digit",
        };

        const formattedTimestamp = note.createdAt
          ? new Intl.DateTimeFormat("en-EN", dateOptions2).format(
              note.createdAt.toDate()
            )
          : "";

        const formattedLastModification = note.updateAt
          ? new Intl.DateTimeFormat("en-EN", dateOptions2).format(
              note.updateAt.toDate()
            )
          : "";

        const formattedDate = note.effectiveDateTime
          ? new Intl.DateTimeFormat("en-EN", dateOptions).format(
              note.effectiveDateTime.toDate()
            )
          : "";

        const formattedTime = note.effectiveDateTime
          ? new Intl.DateTimeFormat("en-EN", timeOptions).format(
              note.effectiveDateTime.toDate()
            )
          : "";

        const noteDate = formatDate(note.shiftStart, dateOptions);
        const noteTime = formatDate(note.shiftStart, timeOptions); // Assurez-vous que c'est bien le champ que vous voulez utiliser
        const noteDateEnd = formatDate(note.shiftEnd, dateOptions);
        const noteDateEndTime = formatDate(note.shiftEnd, timeOptions);

        switch (note.noteType) {
          case "quickNote":
          case "nonTherapyNote":
          case "therapyNote":
          case "supportStaffNote":
          case "aftercareNote":
            return (
              <NotesContainerGlobalMobile
                key={note.id}
                noteContent={note.noteContent}
                effectiveDate={note.effectiveDateTime}
                noteDate={formattedDate}
                noteTime={formattedTime}
                doneBy={note.doneBy}
                timeStamp={formattedTimestamp}
                lastModification={formattedLastModification}
                clientPic={note.clientProfilePicture}
                clientFirstName={note.clientFirstName}
                clientLastName={note.clientLastName}
                noteId={note.id}
                {...note}
              />
            );

          case "endOfShift":
            return (
              <EndOfShiftNoteContainerMobile
                key={note.id}
                noteContent={note.noteContent}
                shiftStartRaw={note.shiftStart}
                shiftEndRaw={note.shiftEnd}
                shiftStart={noteDate}
                shiftEnd={noteDateEnd}
                staff={note.staff}
                doneBy={note.doneBy}
                noteId={note.id}
                noteType={note.noteType}
                noteDate={noteDate} // Date formatée
                noteTime={noteTime} // Heure formatée
                noteDateEndTime={noteDateEndTime}
              />
            );

          case "familyNote":
            return (
              <FamilyNoteContainerMobile
                key={note.id}
                noteContent={note.noteContent}
                effectiveDate={note.effectiveDateTime}
                noteDate={formattedDate}
                noteTime={formattedTime}
                doneBy={note.doneBy}
                timeStamp={formattedTimestamp}
                lastModification={formattedLastModification}
                clientPic={note.clientProfilePicture}
                clientFirstName={note.clientFirstName}
                clientLastName={note.clientLastName}
                noteId={note.id}
                relationship={note.relationShipFamilyNote}
                fileUrls={note.fileUrls}
                {...note}
              />
            );

          case "medicalNote":
            return (
              <MedicalNoteContainerMobile
                key={note.id}
                noteContent={note.noteContent}
                effectiveDate={note.effectiveDateTime}
                noteDate={formattedDate}
                noteTime={formattedTime}
                doneBy={note.doneBy}
                timeStamp={formattedTimestamp}
                lastModification={formattedLastModification}
                clientPic={note.clientProfilePicture}
                clientFirstName={note.clientFirstName}
                clientLastName={note.clientLastName}
                noteId={note.id}
                fileUrls={note.fileUrls}
                {...note}
              />
            );

          case "handoverNote":
            return (
              <HandoverNotesContainerGlobalMobile
                key={note.id}
                noteContent={note.noteContent}
                effectiveDateTime={note.effectiveDateTime}
                noteDate={formattedDate}
                noteTime={formattedTime}
                doneBy={note.doneBy}
                timeStamp={formattedTimestamp}
                lastModification={formattedLastModification}
                noteId={note.id}
                immmigrationAppointment={note.immmigrationAppointment}
                dischargesSwot={note.dischargesSwot}
                newClients={note.newClients}
                medicalInfos={note.medicalInfos}
                hospitalAppointment={note.hospitalAppointment}
                residentialClientMovements={note.residentialClientMovements}
                zoomSessionAssistance={note.zoomSessionAssistance}
                updateAt={note.updateAt}
                createdAt={note.createdAt}
                staffToday={note.staffToday}
                generalAnnouncement={note.generalAnnouncement}
                {...note}
              />
            );

          case "groupNote":
            return (
              <GroupNotesContainerGlobalMobile
                key={note.id}
                noteContent={note.noteContent}
                effectiveDateTime={note.effectiveDateTime}
                noteDate={formattedDate}
                noteTime={formattedTime}
                doneBy={note.doneBy}
                timeStamp={formattedTimestamp}
                lastModification={formattedLastModification}
                clientPic={note.clientProfilePicture}
                clientFirstName={note.clientFirstName}
                clientLastName={note.clientLastName}
                noteId={note.id}
                groupTopic={note.groupTopic}
                groupType={note.typeGroupNote}
                notInAttendance={note.notInAttendance}
                updateAt={note.updateAt}
                createdAt={note.createdAt}
                groupStartDateTime={note.groupStartDateTime}
                {...note}
              />
            );

          default:
            return null;
        }
      })}
    </div>
  );
}

export default AllNotesContainerMobile;
